import Anchor from "atoms/anchors/anchor";
import { HeadingPriority } from "atoms/constants/heading-priority";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import Loader from "atoms/loaders/loader";
import Heading from "atoms/typography/heading";
import Paragraph from "atoms/typography/paragraph";
import AdminEditorPageContextRecord from "models/view-models/situational-navigation/admin-editor-page-context-record";
import CategoryCollectionRecord from "models/view-models/situational-navigation/categories/category-collection-record";
import SolutionRecord from "models/view-models/situational-navigation/solutions/solution-record";
import EditorPageSidebar from "organisms/admin/situational-navigation/editor-page-sidebar";
import BreadcrumbsMenu from "organisms/breadcrumbs/breadcrumbs-menu";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { siteMap } from "internal-sitemap";
import useLoading from "utilities/hooks/use-loading";
import {
    NestedRoutes,
    NestedRoutesProps,
} from "utilities/routing/nested-route";
import AdminCategoryService from "utilities/services/admin/situational-navigation/categories/admin-category-service";
import { ToastManager } from "utilities/toast/toast-manager";
import { SkipNavContent } from "@reach/skip-nav";
import AdminEditorPageContext from "utilities/contexts/admin/use-admin-editor-page-context";
import useCultureCodeRoute from "utilities/hooks/routing/use-culture-code-route";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface SolutionsPageProps extends NestedRoutesProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

export const SolutionsAdminPageContainerId = "solutions-admin-page-container";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const SolutionsPage: React.FC<any> = (props: SolutionsPageProps) => {
    const cssClassName = "c-admin-editor-page-layout";

    const location = useLocation();
    const { generateCultureCodedRoute } = useCultureCodeRoute();

    const { list: getCategoriesApi } = AdminCategoryService.useList();

    const [loadingCategories, setLoadingCategories] = useState(false);
    const loading = useLoading(loadingCategories);
    const [context, setContext] = useState(new AdminEditorPageContextRecord());

    /**
     * Load category options
     */
    useEffect(() => {
        const loadCategories = async () => {
            setLoadingCategories(true);
            try {
                const result = await getCategoriesApi();
                setContext((context: AdminEditorPageContextRecord) =>
                    context.with({
                        categories: CategoryCollectionRecord.fromArray(
                            result.resultObjects
                        ),
                    })
                );
            } catch (e) {
                ToastManager.error(
                    "There was an issue loading category options. Please reload the page to try again."
                );
            }
            setLoadingCategories(false);
        };

        loadCategories();
    }, [getCategoriesApi]);

    return (
        <React.Fragment>
            <BreadcrumbsMenu fullWidth={true} />
            <SkipNavContent tabIndex={-1}>
                <AdminEditorPageContext.Provider value={[context, setContext]}>
                    <div
                        className={`c-dashboard__panel ${cssClassName} c-solutions-admin`}
                        id={SolutionsAdminPageContainerId}>
                        <Heading priority={HeadingPriority.Five}>
                            Situational Navigation Management
                        </Heading>
                        <section className={`${cssClassName}__content`}>
                            {loading && (
                                <Loader accessibleText="Loading Solutions" />
                            )}
                            {!loading && (
                                <React.Fragment>
                                    <EditorPageSidebar type={SolutionRecord} />
                                    <div
                                        className={`${cssClassName}__content__right`}>
                                        <NestedRoutes routes={props.routes} />
                                        {location.pathname ===
                                            generateCultureCodedRoute(
                                                siteMap.admin
                                                    .situationalNavigation
                                                    .solutions.dashboard
                                            ) && (
                                            <div
                                                className={`${cssClassName}__content__right__none-selected`}>
                                                <Paragraph
                                                    size={ParagraphSizes.Large}>
                                                    Select a solution on the
                                                    left to edit or&nbsp;
                                                    <Anchor
                                                        to={
                                                            siteMap.admin
                                                                .situationalNavigation
                                                                .solutions
                                                                .create
                                                        }>
                                                        create a new solution.
                                                    </Anchor>
                                                </Paragraph>
                                            </div>
                                        )}
                                    </div>
                                </React.Fragment>
                            )}
                        </section>
                    </div>
                </AdminEditorPageContext.Provider>
            </SkipNavContent>
        </React.Fragment>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default SolutionsPage;

// #endregion Exports
